

<script setup lang="ts">
const router = useRouter()
const { locale } = useI18n()
const { $api } = useNuxtApp()
const { public: { companyUrl } } = useRuntimeConfig()
const { toast } = useToast()

// Helper for type-safe translations
const lang = computed(() => locale.value as 'en' | 'id')

// 1. Localized Footer Headers
const footerHeaderData = {
    "quicklinks": {
        "title": { "en": "Quick Links", "id": "Tautan Cepat" }
    },
    "contactInfomation": {
        "title": { "en": "Contact Information", "id": "Informasi Kontak" }
    },
    "subscribe": {
        "title": { "en": "Subscribe", "id": "Berlangganan" },
        "label": { 
            "en": "Stay updated with the latest opportunities.", 
            "id": "Dapatkan info terbaru mengenai lowongan kerja." 
        },
        "placeholder": { "en": "Your email address", "id": "Alamat email Anda" },
        "btn": { "en": "Submit", "id": "Kirim" }
    }
}

// 2. Localized Quick Links
const footerQickLinks = computed(() => [
  { 
    label: { en: 'For Job Seeker', id: 'Untuk Pencari Kerja' }[lang.value], 
    path: '/' 
  },
  { 
    label: { en: 'For Company', id: 'Untuk Perusahaan' }[lang.value], 
    path: companyUrl,
        target: '_blank'
  },
])

const legalLinks = {
    "privacy": {
        "en": "Privacy & Policy",
        "id": "Kebijakan Privasi"
    },
    "terms": {
        "en": "Terms & Conditions",
        "id": "Syarat & Ketentuan"
    }
}




export interface ISiteSettings {
  email: string;
  phone_number: string;
  address: string | null;
  whatsapp_url: string;
  landing_show_recent_jobs: boolean;
  instagram_url: string | null;
  facebook_url: string | null;
  linkedin_url: string | null;
}

const isLoadingSubscribe = ref(false)
const email = ref('')



const submit = async () => {
    if (!email.value) {
        const msg = { en: 'Please enter an email address', id: 'Silakan masukkan alamat email' }
        toast(msg[lang.value], 'error')
        return
    }

    isLoadingSubscribe.value = true
    
    try {
        await router.push({
            path: '/verify-newsletter',
            query: {
                email: email.value,
                returnTo: router.currentRoute.value.fullPath
            }
        })
    } catch (e) {
        toast('An unexpected error occurred', 'error')
    } finally {
        isLoadingSubscribe.value = false
    }
}

const { data: siteSettingData } = await $api.master.getSiteSetting()
const siteSetting = computed<ISiteSettings | undefined>(() => siteSettingData.value?.data)


</script>
<template>
    <div class="footer">
        <VContainer>
            <div class="footer__inner">
                <div class="footer__general">
                    <NuxtLink to="/">
                        <div class="footer__logo-container">
                            <img src="/images/footer-logo.svg" class="footer__logo ratio-item" alt="footer logo">
                        </div>
                    </NuxtLink>
                    <div class="footer__social-media">
                        <div class="d-flex ga-3">
                            <VBtn
                                :href="siteSetting?.instagram_url || 'https://instagram.com'"
                                target="_blank"
                                color="error"
                                class="social-btn footer__social-media-btn"
                                aria-label="Instagram"
                                elevation="0"
                                square
                                width="44"
                                height="44"
                            >
                                <i class="text-white text-h3 ri-instagram-line"></i>
                            </VBtn>
                            
                            <VBtn
                                :href="siteSetting?.linkedin_url || 'https://linkedin.com'"
                                target="_blank"
                                color="error"
                                class="social-btn footer__social-media-btn"
                                aria-label="LinkedIn"
                                elevation="0"
                                square
                                width="44"
                                height="44"
                            >
                                <i class="text-white text-h3 ri-linkedin-fill"></i>
                            </VBtn>

                            <VBtn
                                :href="siteSetting?.facebook_url || 'https://facebook.com'"
                                target="_blank"
                                color="error"
                                class="social-btn footer__social-media-btn"
                                aria-label="Facebook"
                                elevation="0"
                                square
                                width="44"
                                height="44"
                            >
                                <i class=" text-white text-h3 ri-facebook-fill"></i>
                            </VBtn>
                        </div>
                    </div>
                </div>
                <div class="footer__qucikLinks">
                    <h3 class="footer__title">
                        {{ footerHeaderData.quicklinks.title[lang] }}
                    </h3>
                    <ul class="footer__menu">
                        <li 
                            v-for="item in footerQickLinks" 
                            :key="item.label" 
                            class="footer__menu-item"
                        >
                            <NuxtLink 
                                :to="item.path" 
                                class="footer__menu-link"
                                :target="item.target || '_self'" 
                            >
                                {{ item.label }}
                            </NuxtLink>
                        </li>
                    </ul>
                </div>
                <div class="footer__contacts">
                    <h3 class="footer__title">
                        {{ footerHeaderData.contactInfomation.title[lang] }}
                    </h3>
                    <ul class="footer__menu">
                        <li class="footer__menu-item">
                            <a :href="`tel:${siteSetting?.phone_number}`" class="footer__menu-link">
                                <i class="footer__menu-icon ri-whatsapp-fill"></i>
                                {{ siteSetting?.phone_number }}
                            </a>
                        </li>
                        <li class="footer__menu-item">
                            <a :href="`mailto:${siteSetting?.email}`" class="footer__menu-link">
                                <i class="footer__menu-icon ri-mail-open-fill"></i>
                                {{ siteSetting?.email }}
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="footer__subscribe">
                    <h3 class="footer__title">
                        {{ footerHeaderData.subscribe.title[lang] }}
                    </h3>
                    <div class="footer__subscribe-wrapper">
                        <label class="footer__subscribe-label">
                            {{ footerHeaderData.subscribe.label[lang] }}
                        </label>
                        <div class="footer__subscribe-group">
                            <VTextField
                                v-model="email"
                                :placeholder="footerHeaderData.subscribe.placeholder[lang]"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                bg-color="white"
                                :disabled="isLoadingSubscribe"
                            />
                            <VBtn
                                color="#1E1E1E"
                                size="large"
                                elevation="0"
                                class="footer__subscribe-btn"
                                :loading="isLoadingSubscribe"
                                @click="submit"
                            >
                                {{ footerHeaderData.subscribe.btn[lang] }}
                            </VBtn>
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="footer__copyright text-medium-emphasis"
            >
                <span class="d-flex gap-x-4 text-primary">
                    <NuxtLink to="/privacy-policy" target="noopener noreferrer">
                        {{ legalLinks.privacy[lang] }}
                    </NuxtLink>
                    <NuxtLink to="/terms-conditions" target="noopener noreferrer">
                       {{ legalLinks.terms[lang] }}
                    </NuxtLink>
                </span>
                <span class="d-flex align-center">
                    &copy;
                    {{ new Date().getFullYear() }}
                    Samurai Rekrutmen
                </span>
            </div>
        </VContainer>
     </div>
</template>
<style lang="scss" scoped>
    :deep(.v-field__input){
        @include vwUnit('min-width',268, 268);
    }
  
    .footer{
        @include imageRatio(256 88,256 88,true,contain,'logo');
        background-color: #fff;
        &__inner{
            width: 100%;
            justify-content: space-between;
            display: grid;
            grid-template-columns: repeat(4, max-content);
            @include vwUnit('padding-top',67, 40);
            @include vwUnit('margin-bottom',67, 50);

            @media only screen and (max-width: 991.98px) {
                grid-template-columns: repeat(3, max-content);
            }
            @media only screen and (max-width: 767.98px) {
                grid-template-columns: 1fr;
                @include vwUnit('gap',40, 50);
                
            }
        }

        &__general{
            @media only screen and (max-width: 991.98px) {
                margin-bottom: 32px;
                grid-column: 1/-1;
                display: flex;
                justify-content: space-between
            }

            @media only screen and (max-width: 767.98px) {
                display: grid;
                 margin-bottom: 0px;
            }
           
        }
        &__logo-container{
            @include vwUnit('width',256, 200);
            @include vwUnit('margin-bottom',50 , 32);
            display: block;
        }

        &__copyright{
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-top: 1px solid #8D8D8D21;
            @include vwUnit('font-size',16, 14);
            @include vwUnit('padding', 24 24 0 24 , 24 24 0 24);
            @media only screen and (max-width: 991.98px) {
                display: grid;
                gap: 8px;
            }
        }
        
        &__title{
            font-family: $primary-font;
            font-weight: 500;
            @include vwUnit('font-size',20, 18);
            @include vwUnit('margin-bottom',26, 20);
            line-height: 1.45;
        }

        &__menu{
            display: grid;
            @include vwUnit('gap',8, 4);
            list-style: none;
        }

        &__menu-item{
            @include vwUnit('font-size',16, 16);
            color: #404040;
            &:hover{
                color: color(primary);
            }
        }

        &__menu-icon{
            @include vwUnit('margin-right',8, 4);
            @include vwUnit('font-size',31, 24);
            position: relative;
            font-size: 31px;
            color: color(primary);
            vertical-align: middle;
        }

        &__subscribe-group{
            display: flex;
            gap: 10px;
            min-width: 380px;
            @media only screen and (max-width: 767.98px) {
                 min-width: unset;
            }
            .v-btn{
                @include  vwUnit('min-width',131, 80);
                @media only screen and (max-width: 767.98px) {
                    min-width: unset;
                }
            }
            .v-field__input{
                @include  vwUnit('min-width',218, 100);
                @media only screen and (max-width: 767.98px) {
                    min-width: unset;
                }
            }
        }

        &__subscribe-label{
            display: block;
            @include vwUnit('font-size',16, 16);
            @include vwUnit('margin-bottom',8, 8);
            font-weight: 400;
            color: #404040;
        }

        &__subscribe-btn{
            display: block;
            height: auto;
            line-height: 1;
            @media only screen and (max-width: 991.98px) {
                height: 50px;
            }
            @media only screen and (max-width: 991.98px) {
            }
        }

        &__social-media-btn{
            min-height: unset !important;
            height: 53px;
            font-size: 32px;

        }
    }
</style>
